import { Organizations } from "@knowfully/ui.common";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  TextField,
  Tooltip,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { useTheme } from "@mui/material/styles";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import ContextOrganizationField, {
  ContextOrganizationFieldMode,
} from "components/ContextOrganizationField";
import DirtyFormSpy from "components/DirtyFormSpy";
import Field from "components/Field";
import FieldRichText from "components/FieldRichText";
import NumericTextField from "components/NumericTextField";
import SelectCorporateEntity from "components/SelectCorporateEntity";
import SelectCreatable from "components/SelectCreatable";
import SelectProduct from "components/SelectProduct";
import SelectProductVersion from "components/SelectProductVersion";
import SelectProductYear from "components/SelectProductYear";
import SelectSku from "components/SelectSku";
import SelectSkuAvailability from "components/SelectSkuAvailability";
import SelectSkuStatus from "components/SelectSkuStatus";
import { startOfDay } from "date-fns";
import { FORM_ERROR } from "final-form";
import { makeStyles } from "helpers/StyleHelper";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import { pick } from "lodash";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { FormProps, withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  ListRequestOfSkuFilter,
  ListResponseOfSkuFormat,
  Product,
  ProductTypes,
  RequestOfGenerateSkuRequest,
  SaveResponse,
  SkuFilter,
  SkuFormat,
  SkuFormatSaveRequest,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { IdName } from "types/common-types";
import {
  SkuFormDetailProps,
  SkuFormValues,
  SkuSidebarSlots,
} from "types/sku-related-types";
import {
  convertDateTimeTo12hrFormat,
  convertToUTC,
  getUtcDate,
  standardDateTimeFormat,
} from "utils/dateUtils";
import translateError from "utils/translateError";
import SelectAdvancedPreparation from "./SelectAdvancedPreparation";
import {
  applyAutoPopulateDefaults,
  assignSkuField,
  getFieldRule,
  isEmptyFormValue,
  isFieldVisible,
  readSkuField,
  SkuFieldKey,
  stripHiddenFields,
  targetSkuFormatName,
} from "./skuFieldVisibilityMap";

const useStyles = makeStyles(() => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  sidebarFeedback: {
    mb: 2,
  },
  sidebarActions: {
    p: 0,
    flexWrap: "wrap",
    rowGap: 1,
  },
}));

const formId = "skuDetailsForm";

const { Form } = withTypes<SkuFormValues>();

const getProductTypeForCurrentValues = (
  values: SkuFormValues,
  selectedProduct: Product | null,
): ProductTypes | undefined =>
  selectedProduct?.id === values.productId
    ? selectedProduct?.productType?.enumValue
    : undefined;

const SkuFormatForm = () => {
  const { formatMessage: t } = useIntl();

  return (
    <Grid container spacing={2}>
      <Field name="name" required>
        {({ input, meta }) => (
          <Grid item xs={12}>
            <TextField
              variant="standard"
              fullWidth
              label={t({ id: "nameLabel" })}
              InputLabelProps={{ required: true }}
              error={meta.error && meta.submitFailed}
              helperText={meta.error && meta.submitFailed && meta.error}
              {...input}
            />
          </Grid>
        )}
      </Field>

      <Field name="acronym" required>
        {({ input, meta }) => (
          <Grid item xs={12}>
            <TextField
              variant="standard"
              fullWidth
              label={t({ id: "skuFormatAcronymLabel" })}
              InputLabelProps={{ required: true }}
              error={meta.error && meta.submitFailed}
              helperText={meta.error && meta.submitFailed && meta.error}
              {...input}
            />
          </Grid>
        )}
      </Field>
    </Grid>
  );
};

const SkuForm = ({
  sku,
  skuId,
  submitLabel,
  onDirty,
  onClean,
  onCancel,
  onSubmit,
  mode,
  onProductIdChange,
  sidebarSlots,
}: SkuFormDetailProps & FormProps<SkuFormValues>) => {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const { skuService } = useGlobalContext();
  const stepValue = ".01";
  const { organizationId } = contextStore.useState();
  const queryClient = useQueryClient();

  const initialValues = useMemo<SkuFormValues>(() => {
    var eventStartDate = convertDateTimeTo12hrFormat(sku.eventStartDate);
    var eventEndDate = convertDateTimeTo12hrFormat(sku.eventEndDate);

    return {
      eventStartDate,
      eventEndDate,
      ...pick(sku ?? {}, [
        "productId",
        "productYearId",
        "productVersionId",
        "latestVersionSkuId",
        "skuFormatId",
        "skuAvailabilityId",
        "skuStatusId",
        "sku",
        "title",
        "description",
        "price",
        "memberPrice",
        "creditLimit",
        "sequence",
        "releaseDate",
        "offlineDate",
        "creditSubmissionExpirationDate",
        "expirationDate",
        "advancePreparationId",
        "corporateEntityId",
        "ownerOrganizationId",
        "credits",
        "deliveryMethodId",
        "hasChild",
      ]),
    };
  }, [sku]);

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const selectedProductRef = useRef<Product | null>(null);
  useEffect(() => {
    selectedProductRef.current = selectedProduct;
  }, [selectedProduct]);

  const { data: allSkuFormatsResp } = useStrictQuery<ListResponseOfSkuFormat>(
    ["allSkuFormats"],
    () =>
      skuService.getSkuFormats(
        ListRequestOfSkuFilter.fromJS({
          pageSize: 200,
          sortField: "name",
        }),
      ),
    {
      staleTime: 5 * 60 * 1000,
    },
  );
  const skuFormatsRef = useRef<SkuFormat[]>([]);
  useEffect(() => {
    skuFormatsRef.current = allSkuFormatsResp?.data ?? [];
  }, [allSkuFormatsResp]);

  const productTypeEnum: ProductTypes | undefined =
    selectedProduct?.productType?.enumValue;

  const isVisible = useCallback(
    (key: SkuFieldKey, values: SkuFormValues) =>
      isFieldVisible(productTypeEnum, key, {
        skuAvailabilityId: values.skuAvailabilityId,
      }),
    [productTypeEnum],
  );

  const requiredFor = useCallback(
    (key: SkuFieldKey) => getFieldRule(productTypeEnum, key).required,
    [productTypeEnum],
  );

  const FetchSkuFormats = useCallback(
    async (searchValue: string) => {
      const result = await queryClient.fetchQuery<ListResponseOfSkuFormat>(
        ["skuFormats", searchValue],
        () =>
          skuService.getSkuFormats(
            ListRequestOfSkuFilter.fromJS({
              sortField: "name",
              parameters: SkuFilter.fromJS({
                searchText: searchValue,
              }),
            }),
          ),
      );

      return {
        result:
          result.data?.map((x) => ({ id: x.id, name: x.name } as IdName)) ?? [],
        relatedSearchValue: searchValue,
      };
    },
    [queryClient, skuService],
  );

  const renderForm = () => {
    const inSidebar = (slot: keyof SkuSidebarSlots, node: ReactNode) => {
      if (sidebarSlots === undefined) return node;
      return sidebarSlots ? createPortal(node, sidebarSlots[slot]) : null;
    };

    const generateSkuHandler = async (
      allValues: SkuFormValues,
      change: (field: "sku", value?: string) => void,
    ) => {
      try {
        var sku = await skuService.generateSku(
          RequestOfGenerateSkuRequest.fromJS({
            parameters: allValues,
          }),
        );
        change("sku", sku?.data);
        onDirty && onDirty();
      } catch (error) {
        console.error(error);
      }
    };

    const submitSave = async (values: SkuFormValues) => {
      try {
        values.eventStartDate = convertToUTC(values.eventStartDate!);
        values.eventEndDate = convertToUTC(values.eventEndDate!);
        values.releaseDate = getUtcDate(values.releaseDate);

        values.expirationDate = getUtcDate(values.expirationDate, {
          endOfDay: true,
        });
        values.offlineDate = getUtcDate(values.offlineDate, { endOfDay: true });

        values.creditSubmissionExpirationDate = getUtcDate(
          values.creditSubmissionExpirationDate,
          { endOfDay: true },
        );

        const productType = getProductTypeForCurrentValues(
          values,
          selectedProductRef.current,
        );

        applyAutoPopulateDefaults(
          productType,
          skuFormatsRef.current,
          (key, value) => {
            const fieldKey = key as SkuFieldKey;
            if (isEmptyFormValue(readSkuField(values, fieldKey))) {
              assignSkuField(values, fieldKey, value);
            }
          },
        );

        const payload = stripHiddenFields(productType, values);

        var response = await onSubmit(payload);

        return response;
      } catch (err: any) {
        console.log(err);
        return { [FORM_ERROR]: translateError(err, t) };
      }
    };

    type NewSkuFormatFormValues = {
      id?: string;
      acronym: string;
      name: string;
      value: string;
    };

    const OnSkuFormatCreationFormSubmit = async (
      values: NewSkuFormatFormValues,
    ) => {
      var response: SaveResponse = await skuService.saveSkuFormat(
        SkuFormatSaveRequest.fromJS(values),
      );

      if (!response.id) {
        throw new Error(t({ id: "skuFormatDeletedSaveFailed" }));
      }

      queryClient.invalidateQueries("skuFormats");
      queryClient.invalidateQueries(["allSkuFormats"]);

      return {
        id: response.id,
        name: values.name,
      } as IdName;
    };

    const validateDates = (values: SkuFormValues) => {
      const errors = {} as any;

      const { releaseDate, offlineDate, expirationDate } = values;

      const releaseDateObj = releaseDate ? startOfDay(releaseDate) : null;
      const offlineDateObj = offlineDate ? startOfDay(offlineDate) : null;
      const expirationDateObj = expirationDate
        ? startOfDay(expirationDate)
        : null;

      const dateValidations = [
        {
          date1: releaseDateObj,
          date2: expirationDateObj,
          errorKey: "releaseDate",
          messageId: "releaseDateErrorMessage",
        },
        {
          date1: expirationDateObj,
          date2: offlineDateObj,
          errorKey: "expirationDate",
          messageId: "expirationDateErrorMessage",
        },
        {
          date1: releaseDateObj,
          date2: offlineDateObj,
          errorKey: "offlineDate",
          messageId: "offlineDateErrorMessage",
        },
      ];

      dateValidations.forEach(({ date1, date2, errorKey, messageId }) => {
        if (date1 && date2 && date1 > date2) {
          errors[errorKey] = t({
            id: messageId,
          });
        }
      });

      return errors;
    };

    const validateForm = (values: SkuFormValues) => {
      let errors = {} as any;
      const productType = getProductTypeForCurrentValues(
        values,
        selectedProductRef.current,
      );
      const eventDatesVisible = isFieldVisible(productType, "eventStartDate", {
        skuAvailabilityId: values.skuAvailabilityId,
      });
      if (eventDatesVisible) {
        if (!values.eventStartDate) {
          errors.eventStartDate = t({ id: "requiredIfLive" });
        }

        if (!values.eventEndDate) {
          errors.eventEndDate = t({ id: "requiredIfLive" });
        }
      }

      if (
        values.eventStartDate &&
        values.eventEndDate &&
        values.eventStartDate > values.eventEndDate
      ) {
        errors.eventStartDate = t({
          id: "eventStartDateCannotBeLaterThanEventEndDate",
        });
      }

      if (
        !skuId &&
        values.ownerOrganizationId === Organizations.KnowfullyLearningGroup
      ) {
        errors.ownerOrganizationId = t({
          id: "rootOrganizationIsNotAllowedTooltip",
        });
      }

      const dateErrors = validateDates(values);
      errors = { ...errors, ...dateErrors };

      return errors;
    };

    return (
      <Form
        onSubmit={submitSave}
        initialValues={initialValues}
        validate={validateForm}
        render={({
          form: { reset, change },
          handleSubmit,
          submitting,
          dirty,
          values,
          submitError,
        }) => {
          const productLoaded =
            !values.productId || selectedProduct?.id === values.productId;
          const requiredFormatName = targetSkuFormatName(
            selectedProduct?.productType?.enumValue,
          );
          const skuFormats = allSkuFormatsResp?.data ?? [];
          const requiredFormatAvailable =
            !requiredFormatName ||
            skuFormats.some((f) => f.name === requiredFormatName);
          const submitDisabled =
            submitting || !dirty || !productLoaded || !requiredFormatAvailable;
          return (
            <form id={formId} onSubmit={handleSubmit}>
              <DirtyFormSpy onDirty={onDirty} onClean={onClean} />
              <Grid container spacing={2}>
                <ContextOrganizationField
                  values={values}
                  mode={
                    skuId
                      ? ContextOrganizationFieldMode.EditDisabled
                      : ContextOrganizationFieldMode.SetByHeader
                  }
                  label="ownerOrganization"
                  formChange={change}
                  organizationFieldName="ownerOrganizationId"
                />

                <Field name="productId">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectProduct
                          {...input}
                          organizationId={organizationId}
                          searchByName={true}
                          label={t({
                            id: "skuFormProductLabel",
                          })}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                          changeProductCallback={(newProduct) => {
                            setSelectedProduct(newProduct);
                            onProductIdChange?.(newProduct?.id);
                            applyAutoPopulateDefaults(
                              newProduct?.productType?.enumValue,
                              allSkuFormatsResp?.data ?? [],
                              change,
                            );
                          }}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                {isVisible("productYearId", values) && (
                  <Field name="productYearId">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectProductYear
                            {...input}
                            label={t({
                              id: "skuFormProductYearLabel",
                            })}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>
                )}

                {isVisible("productVersionId", values) && (
                  <Field name="productVersionId">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectProductVersion
                            {...input}
                            label={t({
                              id: "skuFormProductVersionLabel",
                            })}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>
                )}

                {isVisible("skuFormatId", values) && (
                  <Field
                    name="skuFormatId"
                    required={requiredFor("skuFormatId")}
                  >
                    {({ input, meta }) => {
                      return (
                        <Grid item xs={12}>
                          <SelectCreatable
                            entityFormFields={<SkuFormatForm />}
                            entityName={t({ id: "skuFormSkuFormatLabel" })}
                            idKey="id"
                            labelField="name"
                            fetchOptions={FetchSkuFormats}
                            onEntityFormSubmit={OnSkuFormatCreationFormSubmit}
                            textFieldProps={{
                              label: `${t({ id: "skuFormSkuFormatLabel" })}`,
                              error: meta.error && meta.submitFailed,
                            }}
                            {...input}
                            onChange={(value: IdName | null) =>
                              input.onChange(value?.id ?? null)
                            }
                          />
                        </Grid>
                      );
                    }}
                  </Field>
                )}

                {isVisible("skuAvailabilityId", values) && (
                  <Field
                    name="skuAvailabilityId"
                    required={requiredFor("skuAvailabilityId")}
                  >
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectSkuAvailability
                            label={t({ id: "skuAvailabilityLabel" })}
                            {...input}
                            required={requiredFor("skuAvailabilityId")}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>
                )}

                {inSidebar(
                  "status",
                  <Field
                    name="skuStatusId"
                    required={requiredFor("skuStatusId")}
                  >
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectSkuStatus
                            {...input}
                            label={t({
                              id: "skuFormSkuStatusLabel",
                            })}
                            required={requiredFor("skuStatusId")}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>,
                )}

                <Field name="latestVersionSkuId">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectSku
                          organizationId={organizationId}
                          {...input}
                          label={t({
                            id: "skuFormLatestVersionSkuIdLabel",
                          })}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                <Field name="sku" required>
                  {({ input, meta }) => (
                    <>
                      <Grid item xs={2}>
                        <Tooltip
                          title={
                            values.skuFormatId
                              ? ""
                              : t({
                                  id: "skuFormAutoCompleteTooltip",
                                })
                          }
                          placement="bottom-start"
                        >
                          <span>
                            <Button
                              fullWidth
                              variant="contained"
                              disabled={!values.skuFormatId}
                              onClick={() => generateSkuHandler(values, change)}
                            >
                              {t({ id: "skuFormAutoComplete" })}
                            </Button>
                          </span>
                        </Tooltip>
                      </Grid>
                      <Grid item xs={10}>
                        <TextField
                          variant="standard"
                          fullWidth
                          label={t({ id: "skuLabel" })}
                          InputLabelProps={{ required: true }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    </>
                  )}
                </Field>

                <Field name="title" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "skuFormTitleLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="description">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FieldRichText
                        label={t({ id: "skuFormDescriptionLabel" })}
                        meta={meta}
                        input={input}
                      />
                    </Grid>
                  )}
                </Field>

                {isVisible("price", values) && (
                  <Field name="price">
                    {({ input, meta }) => (
                      <Grid item xs={6}>
                        <NumericTextField
                          variant="standard"
                          fullWidth
                          label={t({ id: "skuFormPriceLabel" })}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>
                )}

                {isVisible("memberPrice", values) && (
                  <Field name="memberPrice">
                    {({ input, meta }) => (
                      <Grid item xs={6}>
                        <NumericTextField
                          variant="standard"
                          fullWidth
                          label={t({ id: "skuFormMemberPriceLabel" })}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>
                )}

                {isVisible("credits", values) && (
                  <Field
                    name="credits"
                    type="number"
                    required={requiredFor("credits")}
                  >
                    {({ input, meta }) => (
                      <Grid item xs={6}>
                        <Tooltip
                          title={t({ id: "skuCreditsBehaviourMessage" })}
                        >
                          <Box>
                            <NumericTextField
                              variant="standard"
                              fullWidth
                              label={t({ id: "skuFormCreditsLabel" })}
                              InputLabelProps={{
                                required: requiredFor("credits"),
                              }}
                              error={meta.error && meta.submitFailed}
                              helperText={meta.error && meta.submitFailed}
                              step={stepValue}
                              {...input}
                            />
                          </Box>
                        </Tooltip>
                      </Grid>
                    )}
                  </Field>
                )}

                {isVisible("creditLimit", values) && (
                  <Field name="creditLimit" type="number">
                    {({ input, meta }) => (
                      <Grid item xs={6}>
                        <Tooltip title={t({ id: "skuFormCreditLimitLabel" })}>
                          <Box>
                            <NumericTextField
                              variant="standard"
                              fullWidth
                              label={t({ id: "skuFormCreditLimitLabel" })}
                              InputLabelProps={{ required: false }}
                              error={meta.error && meta.submitFailed}
                              helperText={meta.error && meta.submitFailed}
                              step={stepValue}
                              {...input}
                            />
                          </Box>
                        </Tooltip>
                      </Grid>
                    )}
                  </Field>
                )}

                {isVisible("sequence", values) && (
                  <Field name="sequence">
                    {({ input, meta }) => (
                      <Grid item xs={6}>
                        <NumericTextField
                          variant="standard"
                          fullWidth
                          label={t({ id: "sequenceLabel" })}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>
                )}

                {isVisible("releaseDate", values) && (
                  <Grid item xs={6}>
                    <Field name="releaseDate" isDate>
                      {({ input, meta }) => (
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <DatePicker
                            label={t({ id: "skuFormReleaseDateLabel" })}
                            format="MM/dd/yyyy"
                            slotProps={{
                              textField: {
                                variant: "standard",
                                error: meta.error && meta.submitFailed,
                                helperText:
                                  meta.error && meta.submitFailed && meta.error,
                              },
                              actionBar: {
                                actions: ["clear", "cancel"],
                              },
                            }}
                            {...input}
                            value={input.value === "" ? null : input.value}
                          />
                        </FormControl>
                      )}
                    </Field>
                  </Grid>
                )}

                {isVisible("offlineDate", values) && (
                  <Grid item xs={6}>
                    <Field name="offlineDate" isDate>
                      {({ input, meta }) => (
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <DatePicker
                            label={t({ id: "skuFormOfflineDateLabel" })}
                            format="MM/dd/yyyy"
                            slotProps={{
                              textField: {
                                variant: "standard",
                                error: meta.error && meta.submitFailed,
                                helperText:
                                  meta.error && meta.submitFailed && meta.error,
                              },
                              actionBar: {
                                actions: ["clear", "cancel"],
                              },
                            }}
                            {...input}
                            value={input.value === "" ? null : input.value}
                          />
                        </FormControl>
                      )}
                    </Field>
                  </Grid>
                )}

                {isVisible("creditSubmissionExpirationDate", values) && (
                  <Grid item xs={6}>
                    <Field name="creditSubmissionExpirationDate" isDate>
                      {({ input, meta }) => (
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <DatePicker
                            label={t({
                              id: "skuFormCreditSubmissionExpirationDateLabel",
                            })}
                            format="MM/dd/yyyy"
                            slotProps={{
                              textField: {
                                variant: "standard",
                                error: meta.error && meta.submitFailed,
                                helperText:
                                  meta.error && meta.submitFailed && meta.error,
                              },
                              actionBar: {
                                actions: ["clear", "cancel"],
                              },
                            }}
                            {...input}
                            value={input.value === "" ? null : input.value}
                          />
                        </FormControl>
                      )}
                    </Field>
                  </Grid>
                )}

                {isVisible("expirationDate", values) && (
                  <Grid item xs={6}>
                    <Field name="expirationDate" isDate>
                      {({ input, meta }) => (
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <DatePicker
                            label={t({ id: "skuFormExpirationDateLabel" })}
                            format="MM/dd/yyyy"
                            slotProps={{
                              textField: {
                                variant: "standard",
                                error: meta.error && meta.submitFailed,
                                helperText:
                                  meta.error && meta.submitFailed && meta.error,
                              },
                              actionBar: {
                                actions: ["clear", "cancel"],
                              },
                            }}
                            {...input}
                            value={input.value === "" ? null : input.value}
                          />
                        </FormControl>
                      )}
                    </Field>
                  </Grid>
                )}

                {isVisible("eventStartDate", values) && (
                  <Grid item xs={6}>
                    <Field name="eventStartDate">
                      {({ input, meta }) => (
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <DateTimePicker
                            label={t({ id: "skuFormEventStartDateLabel" })}
                            format={standardDateTimeFormat}
                            ampm={true}
                            slotProps={{
                              textField: {
                                variant: "standard",
                                error: meta.error && meta.submitFailed,
                                helperText:
                                  meta.error && meta.submitFailed && meta.error,
                              },
                              actionBar: {
                                actions: ["clear", "cancel"],
                              },
                            }}
                            {...input}
                            value={input.value === "" ? null : input.value}
                          />
                        </FormControl>
                      )}
                    </Field>
                  </Grid>
                )}

                {isVisible("eventEndDate", values) && (
                  <Grid item xs={6}>
                    <Field name="eventEndDate">
                      {({ input, meta }) => (
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <DateTimePicker
                            label={t({ id: "skuFormEventEndDateLabel" })}
                            format={standardDateTimeFormat}
                            ampm={true}
                            slotProps={{
                              textField: {
                                variant: "standard",
                                error: meta.error && meta.submitFailed,
                                helperText:
                                  meta.error && meta.submitFailed && meta.error,
                              },
                              actionBar: {
                                actions: ["clear", "cancel"],
                              },
                            }}
                            {...input}
                            value={input.value === "" ? null : input.value}
                          />
                        </FormControl>
                      )}
                    </Field>
                  </Grid>
                )}

                {isVisible("advancePreparationId", values) && (
                  <Field name="advancePreparationId">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectAdvancedPreparation
                            {...input}
                            label={t({
                              id: "skuFormAdvancedPreparationLabel",
                            })}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>
                )}

                {isVisible("corporateEntityId", values) && (
                  <Field name="corporateEntityId">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectCorporateEntity
                            {...input}
                            label={t({
                              id: "skuFormCorporateEntityLabel",
                            })}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>
                )}

                {isVisible("hasChild", values) && (
                  <Field name="hasChild" type="checkbox">
                    {({ input }) => (
                      <Grid item xs={12}>
                        <FormLabel>
                          <T id="skuFormHasChildLabel" />
                        </FormLabel>
                        <Checkbox {...input} />
                      </Grid>
                    )}
                  </Field>
                )}

                {submitError && !sidebarSlots && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>
              <Box mb={2} />
              {inSidebar(
                "actions",
                <>
                  {submitError && sidebarSlots && (
                    <Alert sx={styles.sidebarFeedback} severity="error">
                      {submitError}
                    </Alert>
                  )}
                  <DialogActions sx={sidebarSlots ? styles.sidebarActions : null}>
                    {submitting ? <CircularProgress size={24} /> : null}
                    <Button
                      disabled={submitting || !dirty}
                      onClick={() => {
                        reset();
                        if (onCancel) onCancel();
                      }}
                    >
                      {t({ id: "cancelButton" })}
                    </Button>

                    <Button
                      variant="contained"
                      type="submit"
                      form={formId}
                      color="primary"
                      disabled={submitDisabled}
                    >
                      {submitLabel}
                    </Button>
                  </DialogActions>
                </>,
              )}
            </form>
          );
        }}
      />
    );
  };

  return renderForm();
};

export default SkuForm;
